/**
 * Sound through walls (07 §2): a source with a tall, solid structure between it and the listener
 * is 6 dB down and low-passed. Built once per mission from the same footprints the sim collides
 * with; props the sim reports destroyed stop occluding. Pure, allocation-free queries.
 */

import { collidersFor } from '../sim/props';
import type { MissionDef } from '../sim/types';

/** Only things at least this tall (walls, buildings, bunkers) stand between a sound and an ear. */
const MIN_HEIGHT = 1.8;

interface Occluder {
  propId: number;
  circle: boolean;
  x: number;
  z: number;
  r: number;
  hx: number;
  hz: number;
  alive: boolean;
}

function inside(o: Occluder, x: number, z: number): boolean {
  if (o.circle) return (x - o.x) * (x - o.x) + (z - o.z) * (z - o.z) <= o.r * o.r;
  return Math.abs(x - o.x) <= o.hx && Math.abs(z - o.z) <= o.hz;
}

function crosses(o: Occluder, ax: number, az: number, bx: number, bz: number): boolean {
  const dx = bx - ax;
  const dz = bz - az;
  if (o.circle) {
    const len2 = dx * dx + dz * dz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((o.x - ax) * dx + (o.z - az) * dz) / len2));
    const px = ax + dx * t - o.x;
    const pz = az + dz * t - o.z;
    return px * px + pz * pz <= o.r * o.r;
  }
  // Slab test against the axis-aligned box.
  let t0 = 0;
  let t1 = 1;
  for (let axis = 0; axis < 2; axis++) {
    const origin = axis === 0 ? ax : az;
    const d = axis === 0 ? dx : dz;
    const lo = axis === 0 ? o.x - o.hx : o.z - o.hz;
    const hi = axis === 0 ? o.x + o.hx : o.z + o.hz;
    if (d === 0) {
      if (origin < lo || origin > hi) return false;
      continue;
    }
    const ta = (lo - origin) / d;
    const tb = (hi - origin) / d;
    t0 = Math.max(t0, Math.min(ta, tb));
    t1 = Math.min(t1, Math.max(ta, tb));
    if (t0 > t1) return false;
  }
  return true;
}

export class Occlusion {
  private readonly occluders: Occluder[] = [];

  constructor(def: MissionDef) {
    for (const prop of def.props) {
      for (const c of collidersFor(prop)) {
        if (!c.blocksSight || c.h < MIN_HEIGHT) continue;
        // Tree trunks and posts are tall but far too thin to shadow a sound.
        if (c.shape === 'circle' && c.r < 0.9) continue;
        const circle = c.shape === 'circle';
        this.occluders.push({ propId: c.propId, circle, x: c.x, z: c.z, r: c.r, hx: c.hx, hz: c.hz, alive: true });
      }
    }
  }

  /** A `propDestroyed` event: the rubble no longer blocks anything. */
  destroy(propId: number): void {
    for (const o of this.occluders) if (o.propId === propId) o.alive = false;
  }

  /**
   * Is there a structure between the two points? One that CONTAINS either end does not count:
   * fire from inside a bunker has its own timbre already, and a listener indoors hears normally.
   */
  blocked(ax: number, az: number, bx: number, bz: number): boolean {
    for (const o of this.occluders) {
      if (!o.alive || inside(o, ax, az) || inside(o, bx, bz)) continue;
      if (crosses(o, ax, az, bx, bz)) return true;
    }
    return false;
  }
}
